import { createPageNavigator } from "@-/dev.improve.haniverse.lib.navigator.page";
import { useBrowseLevelsStudyGraphemesQueryOptions } from "#use-browse-levels-study-graphemes-query";
import { useCallback } from "react";

const LEVELS_BASE = "/(nav)/(app)/browse/(browse-tabs)/levels" as const;

/**
 * Levels tab entry — resolves to the levels folder, whose layout renders a `Slot` that focuses its
 * index completion route (All Levels).
 */
function useNavigator() {
  const studyGraphemesQueryOptions = useBrowseLevelsStudyGraphemesQueryOptions();

  return useCallback(
    () => ({
      href: {
        pathname: LEVELS_BASE,
      },
      // Completion reads this compact list. Individual mnemonic details are loaded only
      // when their tiles or details screens need them, never for the entire studied deck.
      queries: [studyGraphemesQueryOptions],
    }),
    [studyGraphemesQueryOptions],
  );
}

export const useBrowseLevelsPageNavigator = createPageNavigator<void>({
  useNavigator,
});

/**
 * The completion sub-routes (All / Incomplete / Completed) all read the same shared levels layout
 * context, so each warms the identical studied-grapheme list on navigation. The toolbar's
 * completion pills call these navigators' `replace.mutate()` to switch between the sibling routes
 * (mirroring the Characters toolbar), so the layout can render a plain `Slot` instead of a nested
 * `MaterialTopTabs` pager — a pager nested inside the parent browse pager fails to mount its initial
 * scene, leaving the Levels tab stuck on an empty skeleton.
 */
function useIncompleteNavigator() {
  const studyGraphemesQueryOptions = useBrowseLevelsStudyGraphemesQueryOptions();

  return useCallback(
    () => ({
      href: { pathname: `${LEVELS_BASE}/incomplete` },
      queries: [studyGraphemesQueryOptions],
    }),
    [studyGraphemesQueryOptions],
  );
}

export const useBrowseLevelsIncompletePageNavigator = createPageNavigator<void>({
  useNavigator: useIncompleteNavigator,
});

function useCompleteNavigator() {
  const studyGraphemesQueryOptions = useBrowseLevelsStudyGraphemesQueryOptions();

  return useCallback(
    () => ({
      href: { pathname: `${LEVELS_BASE}/complete` },
      queries: [studyGraphemesQueryOptions],
    }),
    [studyGraphemesQueryOptions],
  );
}

export const useBrowseLevelsCompletePageNavigator = createPageNavigator<void>({
  useNavigator: useCompleteNavigator,
});
